import md5 from 'md5';
import type { PayuConfirmationPayload, PaymentOrder } from '../api/types';

export type PaymentHeader = {
  title: string;
  description: string;
};

export type PreparedPaymentOrder = PaymentOrder & {
  tax: number;
  total: number;
  buyerFullName: string;
  buyerEmail: string;
  description: string;
  signature: string;
  merchantId: string;
  url: string;
  referenceCode: string;
};

const PAYMENT_HEADERS: Record<'default' | 'approved' | 'declined' | 'unknown', PaymentHeader> = {
  default: {
    title: 'Realizar Pago.',
    description:
      'Su cuenta se encuentra suspendida. Por favor realice el pago y continúe con nuestros servicios.',
  },
  approved: {
    title: 'Pago Aprobado!',
    description: 'El pago se ha recibido satisfactoriamente.',
  },
  declined: {
    title: 'Pago declinado',
    description: 'Lo sentimos, el pago no se ha aprobado. Verifica los datos e inténtalo de nuevo.',
  },
  unknown: {
    title: 'Estado del pago',
    description: 'Estamos validando el resultado de tu transacción.',
  },
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const getPaymentHeader = (transactionState: number | null): PaymentHeader => {
  if (transactionState === null) return PAYMENT_HEADERS.default;
  if (transactionState === 4) return PAYMENT_HEADERS.approved;
  if (transactionState === 6) return PAYMENT_HEADERS.declined;
  return PAYMENT_HEADERS.unknown;
};

export const buildPayuConfirmationPayload = (
  search: string
): { payload: PayuConfirmationPayload; transactionState: number } | null => {
  const params = new URLSearchParams(search);
  const transactionState = params.get('transactionState');
  if (!transactionState) return null;

  const merchantId = params.get('merchantId');
  const referenceCode = params.get('referenceCode');
  const txValue = params.get('TX_VALUE');
  const currency = params.get('currency');
  const signature = params.get('signature');

  if (!merchantId || !referenceCode || !txValue || !currency || !signature) {
    return null;
  }

  return {
    payload: {
      merchant_id: merchantId,
      reference_sale: referenceCode,
      value: txValue,
      currency,
      state_pol: transactionState,
      sign: signature,
    },
    transactionState: toNumber(transactionState),
  };
};

export const buildPreparedPaymentOrder = ({
  order,
  buyerFullName,
  buyerEmail,
}: {
  order: PaymentOrder;
  buyerFullName: string;
  buyerEmail: string;
}): PreparedPaymentOrder => {
  const merchantId =
    (typeof order.merchandId === 'string' && order.merchandId) ||
    (typeof order.merchantId === 'string' && order.merchantId) ||
    '';
  const referenceCode = typeof order._id === 'string' ? order._id : '';
  const amount = toNumber(order.amount);
  const tax = 0;
  const total = amount + tax;
  const key = typeof order.key === 'string' ? order.key : '';
  const signature = md5(`${key}~${merchantId}~${referenceCode}~${total}~COP`);
  const url = typeof order.url === 'string' ? order.url : '';

  return {
    ...order,
    tax,
    total,
    buyerFullName,
    buyerEmail,
    description: 'Pago para daater',
    signature,
    merchantId,
    url,
    referenceCode,
  };
};

export const formatPaymentAmount = (value: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return value.toLocaleString('es-CO');
};
